import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StepComponent, StepsComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { CompanyDocument } from '@app/models/documents/company-document.model';
import { OfferLetter } from '@app/models/offer-letters/offer-letter.model';
import { DocumentType } from '@app/modules/documents-v2/enums/document-type.enum';
import { LetterTemplateForm } from '@app/modules/offer-letter/forms/candidate/components';
import {
    MergeFieldFormInputAttributes,
    displayMergeField,
} from '@app/modules/offer-letter/functions/offer-letter-field-form-input-attributes';
import { OfferLettersService } from '@app/modules/offer-letter/services';
import {
    CANDIDATE_INDEX,
    CANDIDATE_SHOW,
    EMPLOYEE_INDEX,
    OfferLetterCreationReferer,
    TEMPLATE_SHOW,
} from '@app/modules/offer-letter/types/offer-letter-referer-view.type';
import { AnalyticService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { HelloSignEmbedService } from '@app/services/hello-sign-embed.service';
import { TranslatableKey } from '@app/types/translatable.type';

@Component({
    templateUrl: './offer-letter-store.template.html',
    providers: [HelloSignEmbedService],
})
export class OfferLetterStoreView implements OnInit {
    @ViewChild('appSteps', { static: true }) stepsComponent: StepsComponent;
    @ViewChild('candidateInformationStep', { static: true }) candidateInformationStep: StepComponent;
    @ViewChild('letterTemplateStep', { static: true }) letterTemplateStep: StepComponent;
    @ViewChild('letterDetailsStep', { static: true }) letterDetailsStep: StepComponent;
    @ViewChild('reviewStep', { static: true }) reviewStep: StepComponent;
    @ViewChild('letterTemplateForm', { static: true }) letterTemplateForm: LetterTemplateForm;

    isLoading = false;
    isLetterDetailsStepLoading = false;
    allowOfferLetterPreviews = false;

    title: TranslatableKey = 'offer-letter.sendOfferLetter';
    referer: OfferLetterCreationReferer | null = null; // The view that the user was on before this view
    steps: StepComponent[] = [];
    offerLetter: OfferLetter = new OfferLetter();
    offerLetterTemplates: CompanyDocument[];
    mergeFieldFormInputAttributes: MergeFieldFormInputAttributes[] = [];

    offerLetterHasCosigners = false;

    constructor(
        private notify: NotifyService,
        private router: Router,
        private route: ActivatedRoute,
        private offerLettersService: OfferLettersService,
        private analyticsService: AnalyticService,
        private helloSign: HelloSignEmbedService,
        private featureService: FeatureService
    ) {}

    // this function is passed to the app steps component
    // we use an arrow function to force binding the "this" property
    beforeNext = async (): Promise<void> => {
        const currentStepKey = this.stepsComponent.step.stepKey;

        if (currentStepKey === 'letterTemplate') {
            await this.setMergeFieldFormInputAttributes(this.letterTemplateForm.selectedOfferLetterTemplate);
        }
    };

    async ngOnInit(): Promise<void> {
        this.allowOfferLetterPreviews = await this.featureService.has(FeatureFlag.offerLetterPreviews);

        this.setSteps();
        await this.getOfferLetterTemplates();

        this.populateFromQueryParams();
        this.setReferer();

        // On revise offer letter flow, we need to fetch the offer letter.
        if (this.route.snapshot.data.models) {
            const offerLetterDetails = this.route.snapshot.data.models.get('offerLetter');

            if (!offerLetterDetails) {
                return;
            }

            this.title = 'offer-letter.reviseOfferLetter';

            this.populateFieldsDuringReviseOfferLetterFlow(offerLetterDetails);
        }
    }

    async onSave(): Promise<void> {
        this.isLoading = true;

        try {
            // nullify companyDocument to prevent circular serialization issue
            this.offerLetter.signingTemplate.document = null;

            if (this.offerLetter.signingTemplate.hasCosigners()) {
                this.offerLetter.signerSlots = this.offerLettersService.buildSignerSlots(
                    this.offerLetter.signingTemplate.slots
                );
            }

            await this.offerLetter.save();
            this.notify.success('offer-letter.offerLetterSentNotification');
            this.trackEvent();
            this.router.navigate(['/employees', 'offer-letters', 'candidates']);
            this.isLoading = false;
        } catch (err) {
            this.notify.error(err.message || 'offer-letter.offerLetterSendErrorNotification');
            this.isLoading = false;
        }
    }

    async previewOfferLetter(): Promise<void> {
        this.isLoading = true;

        const claimUrl = await this.offerLettersService.getClaimUrl(this.offerLetter);

        if (!claimUrl) {
            this.notify.error('offer-letter.unableToPreviewOfferLetter');
            this.isLoading = false;
            return;
        }

        this.analyticsService.trackEvent(AnalyticEvents.OfferLetterTemplatePreviewed);

        /**
         *  If the user clicks on "Send" within the HelloSign iframe, we'll use the
         *  wasSent() event to emit an event to the parent to send the Offer Letter.
         */
        this.helloSign.onSendEventOnce(async () => {
            this.analyticsService.trackEvent(AnalyticEvents.OfferLetterTemplatePreviewSend);
            await this.onSave();
        });

        await this.helloSign.open(claimUrl, null, true);
        this.isLoading = false;
    }

    private setSteps(): void {
        this.steps = [this.candidateInformationStep, this.letterTemplateStep, this.letterDetailsStep, this.reviewStep];
    }

    private async getOfferLetterTemplates(): Promise<void> {
        this.isLoading = true;

        const [offerLetterTemplates] = await CompanyDocument.with([
            'file',
            'signingTemplate',
            'signingTemplate.slots',
            'signingTemplate.slots.assignee.job',
            'signingTemplate.document',
        ])
            .where('kind', DocumentType.offerLetter)
            .all();

        // This filters out signing templates that do not have a helloSignTemplateId
        // This accounts for invalid data which should not exist, but sadly might
        const validTemplates = offerLetterTemplates.filter(
            (o: CompanyDocument) => o.signingTemplate.helloSignTemplateId
        );

        this.offerLetterTemplates = validTemplates;

        this.isLoading = false;
    }

    private async setMergeFieldFormInputAttributes(selectedOfferLetterTemplate: CompanyDocument): Promise<void> {
        this.isLetterDetailsStepLoading = true;

        try {
            const allMergeFields = await this.offerLettersService.getMergeFields(selectedOfferLetterTemplate);
            const validMergeFields = allMergeFields.filter((mergeField) => displayMergeField(mergeField));

            this.mergeFieldFormInputAttributes =
                this.offerLettersService.buildMergeFieldFormInputAttributes(validMergeFields);
        } catch (err) {
            this.notify.error('offer-letter.invalidTemplateCouldNotFetchMergeFields');
        }

        this.isLetterDetailsStepLoading = false;
    }

    /**
     * If there are query params in the url, this means the origin
     * is asking this view to pe prepopulated with certain fields
     */
    private populateFromQueryParams(): void {
        const queryParams = this.route.snapshot.queryParams;

        this.offerLetter.legalFirstName = queryParams.firstName ?? null;
        this.offerLetter.legalLastName = queryParams.lastName ?? null;
        this.offerLetter.email = queryParams.email ?? null;
        this.offerLetter.atsApplicantId = queryParams.atsApplicantId ?? null;

        /**
         * set the signing template from the query string
         */
        if (queryParams.signingTemplateId) {
            const companyDocument = this.offerLetterTemplates.find(
                (o) => o.signingTemplate.id == queryParams.signingTemplateId
            );

            const signingTemplate = companyDocument?.signingTemplate;

            if (signingTemplate) {
                this.offerLetter.signingTemplateId = signingTemplate.id;
                this.offerLetter.signingTemplate = signingTemplate;
            }
        }
    }

    private populateFieldsDuringReviseOfferLetterFlow(offerLetterDetails: OfferLetter): void {
        this.offerLetter.legalFirstName = offerLetterDetails.legalFirstName;
        this.offerLetter.legalLastName = offerLetterDetails.legalLastName;
        this.offerLetter.email = offerLetterDetails.email;
        this.offerLetter.candidateId = offerLetterDetails.candidateId;
        this.offerLetter.reportsToId = offerLetterDetails.reportsToId ?? null;
        this.offerLetter.reportsTo = offerLetterDetails.reportsTo ?? null;

        this.offerLetter.signingTemplateId = offerLetterDetails.signingTemplateId;
        this.offerLetter.signingTemplate = offerLetterDetails.signingTemplate;

        this.offerLetter.position = offerLetterDetails.position ?? null;
        this.offerLetter.department = offerLetterDetails.department ?? null;
        this.offerLetter.location = offerLetterDetails.location ?? null;

        this.offerLetter.employmentType = offerLetterDetails.employmentType ?? null;
        this.offerLetter.compensationRate = offerLetterDetails.compensationRate ?? null;
        this.offerLetter.compensationFrequency = offerLetterDetails.compensationFrequency ?? null;
        this.offerLetter.stockOptions = offerLetterDetails.stockOptions ?? null;
        this.offerLetter.probationPeriod = offerLetterDetails.probationPeriod ?? null;
        this.offerLetter.contractTerm = offerLetterDetails.contractTerm ?? null;
        this.offerLetter.hoursPerWeek = offerLetterDetails.hoursPerWeek ?? null;
        this.offerLetter.firstDayOfWorkOn = offerLetterDetails.firstDayOfWorkOn ?? null;
        this.offerLetter.contractEndsOn = offerLetterDetails.contractEndsOn ?? null;
        this.offerLetter.vacationDays = offerLetterDetails.vacationDays ?? null;
        this.offerLetter.expiresOn = offerLetterDetails.expiresOn ?? null;
        this.offerLetter.candidateAddress = offerLetterDetails.candidateAddress ?? null;
    }

    /**
     * Determine which pendo event to send based on url query param
     * the query param is stored in the 'referer'
     */
    private trackEvent(): void {
        // standard way of creating an offer letter
        // there will be no referer if they go directly to this url
        if (!this.referer || this.referer === CANDIDATE_INDEX) {
            this.analyticsService.trackEvent(AnalyticEvents.OfferLetterSendOfferLetterViaOffersSection);
            return;
        }

        // revising is done from the candidate show view
        if (this.referer === CANDIDATE_SHOW) {
            this.analyticsService.trackEvent(AnalyticEvents.OfferLetterReviseOfferLetter);
        }

        if (this.referer === EMPLOYEE_INDEX) {
            this.analyticsService.trackEvent(AnalyticEvents.OfferLetterSendOfferLetterViaNewEmployeeFlow);
        }

        if (this.referer === TEMPLATE_SHOW) {
            this.analyticsService.trackEvent(AnalyticEvents.OfferLetterSendOfferLetterViaTemplateView);
        }
    }

    private setReferer(): void {
        const queryParams = this.route.snapshot.queryParams;

        this.referer = queryParams.referer ?? null;
    }
}
